import { useState, useEffect } from 'react';
import axios from 'axios';
import AdminLayout from '@/components/AdminLayout';
import ImageUpload from '@/components/ImageUpload';
import { API } from '@/App';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { toast } from 'sonner';
import { Plus, Edit, Trash2 } from 'lucide-react';

const AdminCertificates = () => {
  const [certificates, setCertificates] = useState([]);
  const [loading, setLoading] = useState(false);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingCert, setEditingCert] = useState(null);
  const [formData, setFormData] = useState({
    title_tr: '',
    title_en: '',
    issuer: '',
    issue_date: '',
    image_url: '',
    order: 0,
  });

  useEffect(() => {
    fetchCertificates();
  }, []);

  const fetchCertificates = async () => {
    try {
      const response = await axios.get(`${API}/certificates`);
      setCertificates(response.data);
    } catch (error) {
      console.error('Error fetching certificates:', error);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      if (editingCert) {
        await axios.put(`${API}/certificates/${editingCert.id}`, formData);
        toast.success('Sertifika güncellendi!');
      } else {
        await axios.post(`${API}/certificates`, formData);
        toast.success('Sertifika eklendi!');
      }
      fetchCertificates();
      setDialogOpen(false);
      resetForm();
    } catch (error) {
      console.error('Error saving certificate:', error);
      toast.error('Bir hata oluştu!');
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Bu sertifikayı silmek istediğinizden emin misiniz?')) return;

    try {
      await axios.delete(`${API}/certificates/${id}`);
      toast.success('Sertifika silindi!');
      fetchCertificates();
    } catch (error) {
      console.error('Error deleting certificate:', error);
      toast.error('Bir hata oluştu!');
    }
  };

  const handleEdit = (cert) => {
    setEditingCert(cert);
    setFormData({
      title_tr: cert.title_tr,
      title_en: cert.title_en,
      issuer: cert.issuer,
      issue_date: cert.issue_date,
      image_url: cert.image_url,
      order: cert.order,
    });
    setDialogOpen(true);
  };

  const resetForm = () => {
    setEditingCert(null);
    setFormData({
      title_tr: '',
      title_en: '',
      issuer: '',
      issue_date: '',
      image_url: '',
      order: 0,
    });
  };

  const handleChange = (e) => {
    const value = e.target.type === 'number' ? parseInt(e.target.value) : e.target.value;
    setFormData({ ...formData, [e.target.name]: value });
  };

  return (
    <AdminLayout>
      <div>
        <div className="flex justify-between items-center mb-8">
          <h1 
            className="text-3xl font-bold text-gray-900"
            style={{ fontFamily: 'Space Grotesk, sans-serif' }}
            data-testid="certificates-admin-title"
          >
            Sertifikalar Yönetimi
          </h1>
          <Dialog open={dialogOpen} onOpenChange={(open) => { setDialogOpen(open); if (!open) resetForm(); }}>
            <DialogTrigger asChild>
              <Button data-testid="add-certificate-btn">
                <Plus className="w-4 h-4 mr-2" />
                Yeni Sertifika
              </Button>
            </DialogTrigger>
            <DialogContent className="max-w-2xl">
              <DialogHeader>
                <DialogTitle>{editingCert ? 'Sertifika Düzenle' : 'Yeni Sertifika Ekle'}</DialogTitle>
              </DialogHeader>
              <form onSubmit={handleSubmit} className="space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label htmlFor="title_tr">Başlık (TR)</Label>
                    <Input
                      id="title_tr"
                      name="title_tr"
                      value={formData.title_tr}
                      onChange={handleChange}
                      required
                      data-testid="cert-title-tr"
                    />
                  </div>
                  <div>
                    <Label htmlFor="title_en">Title (EN)</Label>
                    <Input
                      id="title_en"
                      name="title_en"
                      value={formData.title_en}
                      onChange={handleChange}
                      required
                      data-testid="cert-title-en"
                    />
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label htmlFor="issuer">Veren Kurum</Label>
                    <Input
                      id="issuer"
                      name="issuer"
                      value={formData.issuer}
                      onChange={handleChange}
                      required
                      data-testid="cert-issuer"
                    />
                  </div>
                  <div>
                    <Label htmlFor="issue_date">Tarih</Label>
                    <Input
                      id="issue_date"
                      name="issue_date"
                      value={formData.issue_date}
                      onChange={handleChange}
                      required
                      placeholder="2024"
                      data-testid="cert-date"
                    />
                  </div>
                </div>
                <ImageUpload
                  label="Sertifika Görseli"
                  value={formData.image_url}
                  onChange={(url) => setFormData({ ...formData, image_url: url })}
                />
                <div>
                  <Label htmlFor="order">Sıra</Label>
                  <Input
                    id="order"
                    name="order"
                    type="number"
                    value={formData.order}
                    onChange={handleChange}
                    data-testid="cert-order"
                  />
                </div>
                <Button type="submit" disabled={loading} className="w-full" data-testid="cert-submit">
                  {loading ? 'Kaydediliyor...' : 'Kaydet'}
                </Button>
              </form>
            </DialogContent>
          </Dialog>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {certificates.map((cert) => (
            <div key={cert.id} className="bg-white rounded-xl border border-gray-200 overflow-hidden" data-testid={`cert-card-${cert.id}`}>
              <img src={cert.image_url} alt={cert.title_tr} className="w-full h-48 object-cover" />
              <div className="p-6">
                <h3 className="text-lg font-bold text-gray-900 mb-2">{cert.title_tr}</h3>
                <p className="text-sm text-gray-600 mb-1"><strong>Veren:</strong> {cert.issuer}</p>
                <p className="text-sm text-gray-600 mb-4"><strong>Tarih:</strong> {cert.issue_date}</p>
                <div className="flex gap-2">
                  <Button size="sm" variant="outline" onClick={() => handleEdit(cert)} data-testid={`edit-cert-${cert.id}`}>
                    <Edit className="w-4 h-4 mr-1" />
                    Düzenle
                  </Button>
                  <Button size="sm" variant="destructive" onClick={() => handleDelete(cert.id)} data-testid={`delete-cert-${cert.id}`}>
                    <Trash2 className="w-4 h-4 mr-1" />
                    Sil
                  </Button>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </AdminLayout>
  );
};

export default AdminCertificates;